/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */

import type {
  Dashboard,
  DashboardContext,
  DashboardFilter,
  QuickView,
  ReadDashboardsApiResponse,
  ReadFiltersApiResponse,
  ReadInstrumentsApiResponse,
  ReadQuickViewApiResponse,
  ReadQuickViewsApiResponse,
} from 'lib-analytics-types';
import kebabCase from 'lodash/kebabCase';
import { DeepWritable, Writable } from 'ts-essentials';
import { v4 as uuidv4 } from 'uuid';

import { sortPanels } from './utils/sortPanels';

function transformFilter(
  filter?: Writable<DashboardFilter | DashboardContext>,
) {
  if (!filter) return;
  filter.label = filter.label ?? filter.field;
  filter.values = filter.values.filter((item) => item.label);
}

/**
 * Returns the transformed dashboards response.
 *
 * @param response The raw response from the dashboards endpoint.
 */
export function transformReadDashboardsResponse(
  response: any,
): ReadDashboardsApiResponse {
  response.data.dashboards.forEach((dashboard: DeepWritable<Dashboard>) => {
    // Set default values for missing properties.
    dashboard.slug = dashboard.slug ?? kebabCase(dashboard.title);
    dashboard.icon = dashboard.icon ?? 'chart-bar';
    dashboard.displayable = dashboard.displayable ?? true;
    dashboard.tier = dashboard.tier ?? 'analytics';

    // Transform the regular filters.
    dashboard.filters.forEach(transformFilter);

    // Transform the optional context filter.
    if (dashboard.context) {
      transformFilter(dashboard.context);

      // The `is_empty` operator does not make sense for context filters.
      dashboard.context.operators =
        dashboard.context?.operators.filter((o) => o !== 'is_empty') ?? [];
    }

    // Sort the panels and set the dashboard_id on each panel.
    dashboard.panels
      .sort(sortPanels)
      .forEach((panel) => (panel.dashboard_id = dashboard.id));
  });

  return response;
}

/**
 * Returns the transformed filters response.
 *
 * @param response The raw response from the filters endpoint.
 */
export function transformReadFiltersResponse(
  response: any,
): ReadFiltersApiResponse {
  response.data.filters.forEach(transformFilter);
  return response;
}

/**
 * Returns the transformed instruments response.
 *
 * @param response The raw response from the instruments endpoint.
 */
export function transformReadInstrumentsResponse(
  response: any,
  meta?: any,
): ReadInstrumentsApiResponse {
  response.meta = response.meta ?? {};
  response.meta.request_id = meta?.response?.headers?.get('X-Dixa-Rid');

  return response;
}

/**
 * Returns the transformed quick view response.
 *
 * @param response The raw response from the quick view endpoint.
 */
export function transformReadQuickViewResponse(
  response: any,
): ReadQuickViewApiResponse {
  transformQuickView(response.data.quick_view);
  return response;
}

/**
 * Returns the transformed quick views response.
 *
 * @param response The raw response from the quick views endpoint.
 */
export function transformReadQuickViewsResponse(
  response: any,
): ReadQuickViewsApiResponse {
  response.data.quick_views.forEach(transformQuickView);
  return response;
}

/**
 * Transforms the quick view object and normalizes the panels list.
 *
 * The backend returns the quick view panels as an array of objects roughly in
 * the shape of { panel: {}, layout: {}, controls: {}, dashboard_id: '',
 * description: '' }.
 *
 * To make it easier to work with in the frontend throughout the component tree,
 * we flatten the structure into a single panel object of type InstrumentPanel.
 *
 * Each quick view panel has a `instance_id`, either returned by the backend or
 * generated on the frontend. This is used to differentiate between multiple
 * instances of the same dashboard panel in quick views.
 */
function transformQuickView(quickView: any): QuickView {
  quickView.panels = (quickView.panels ?? [])
    .map(
      ({
        panel,
        instance_id = uuidv4(),
        dashboard_id,
        description,
        layout,
        controls,
      }) => ({
        ...panel,
        instance_id,
        dashboard_id,
        description,
        layout,
        controls,
      }),
    )
    .sort(sortPanels);

  return quickView;
}
